import { Box, Button, Dialog, DialogActions, Stack, Typography, Alert, Collapse } from "@mui/material";
import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import {
	footwearMaterialColumns,
	upperMaterialColumn,
	upperMaterialTypeOptions,
	upperMaterialPatternOptions,
	soleMaterialOptions,
	insocksOptions,
} from "../data";
import { arrType, inputTypes, selectOptionsKeyName } from "data/globalData";
import { useEffect, useState } from "react";
import { getColDefaultValue } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import axios from "axios";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";

const impactAlertSx = {
	boxSizing: "border-box",
	width: "100%",
	maxWidth: "100%",
	alignItems: "flex-start",
	"& .MuiAlert-message": {
		flex: 1,
		minWidth: 0,
		wordBreak: "break-word",
		overflowWrap: "anywhere",
	},
	"& .MuiAlert-action": {
		flexShrink: 0,
		ml: 1,
		pt: 0.25,
	},
};

const CreateOrUpdateMaterialModal = ({
	open,
	setOpen,
	handleSaveFunction = () => {},
	mode = "create",
	selectedRow,
	setSelectedRow,
}) => {
	const { snackBar, setSnackBarOpen, setSnackBar } = GlobalState();

	const defPageData = {
		...Object.values(footwearMaterialColumns)
			.map((col) => ({ [col.key]: getColDefaultValue(col) }))
			.reduce((acc, row) => ({ ...acc, ...row }), {}),
		[upperMaterialColumn.key]: getColDefaultValue(upperMaterialColumn),
	};

	const [pageData, setPageData] = useState(defPageData);
	const [errorObj, setErrorObj] = useState({});
	const [impact, setImpact] = useState(null);
	const [impactLoading, setImpactLoading] = useState(false);
	const [impactError, setImpactError] = useState(null);
	const [showImpactStyles, setShowImpactStyles] = useState(false);

	useEffect(() => {
		if (mode !== "update" || !selectedRow?.fw_material_code) {
			setImpact(null);
			setImpactLoading(false);
			setImpactError(null);
			setShowImpactStyles(false);
			return;
		}

		const fetchImpact = async () => {
			setImpactLoading(true);
			setImpactError(null);
			setImpact(null);
			setShowImpactStyles(false);
			try {
				const res = await axios.get(`/api/sourcing/footwear_material_master/impact/${selectedRow.fw_material_code}`);
				setImpact(res.data.result);
			} catch (error) {
				setImpact(null);
				setImpactError(parseErrorMessage(error));
			} finally {
				setImpactLoading(false);
			}
		};

		fetchImpact();
	}, [mode, selectedRow?.fw_material_code]);

	useEffect(() => {
		if (mode === "update" && selectedRow) {
			setPageData(selectedRow);
		}
	}, [selectedRow, mode]);

	const handleCloseModal = () => {
		setOpen(false);
		setErrorObj({});
		setImpact(null);
		setImpactLoading(false);
		setImpactError(null);
		setShowImpactStyles(false);
		setPageData(defPageData);
		setSelectedRow();
	};

	const handleSaveClick = () => {
		let errObj = {};

		if (!pageData[upperMaterialColumn.key]) {
			errObj = { ...errObj, [upperMaterialColumn.key]: `${upperMaterialColumn.name} is required` };
		}

		Object.values(footwearMaterialColumns).forEach((col) => {
			if (col.input_type === inputTypes.AUTO_GENERATE.key) return;
			if (col.constraints.NOTNULL && !pageData[col.key]) {
				errObj = { ...errObj, [col.key]: `${col.name} is required` };
			}
		});

		if (Object.keys(errObj).length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "warning",
				message: `Please fill the required fields`,
			});
			setErrorObj(errObj);
			return;
		}

		setErrorObj({});
		if (mode === "update" && !impactLoading && !impactError && impact?.total_count > 0) {
			const confirmed = window.confirm(
				`This change will update material details on ${impact.total_count} style(s)${impact.published_count ? ` (${impact.published_count} published)` : ""}. Continue?`,
			);
			if (!confirmed) return;
		}
		if (typeof handleSaveFunction === "function") handleSaveFunction(pageData);
	};

	return (
		<Dialog fullWidth maxWidth="md" open={open} onClose={handleCloseModal} scroll="body">
			<Stack width="100%" p={2} boxSizing="border-box" spacing={2}>
				<Typography variant="h6">{mode === "create" ? "Create" : "Update"} Footwear Material</Typography>

				<Box display="flex" width="100%" rowGap={2} flexWrap="wrap" columnGap="3%">
					<RenderInputField
						column={footwearMaterialColumns.fw_material_code}
						data={pageData}
						setData={() => {}}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[footwearMaterialColumns.fw_material_code.key]}
					/>
					<RenderInputField
						column={{
							...upperMaterialColumn,
							input_type: mode === "create" ? inputTypes.SELECT.key : inputTypes.AUTO_GENERATE.key,
						}}
						data={pageData}
						setData={setPageData}
						onChangeCallBack={(e, value, updated) => {
							setPageData({
								...updated,
								[footwearMaterialColumns.upper_material_type.key]: "",
								[footwearMaterialColumns.upper_material_pattern.key]: "",
								[footwearMaterialColumns.sole_material.key]: "",
								[footwearMaterialColumns.insocks.key]: "",
							});
						}}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[upperMaterialColumn.key]}
					/>
					<RenderInputField
						column={{
							...footwearMaterialColumns.upper_material_type,
							[selectOptionsKeyName]: {
								type: arrType,
								list: pageData?.[upperMaterialColumn.key]
									? upperMaterialTypeOptions.filter(
											(item) => item[upperMaterialColumn.key] === pageData[upperMaterialColumn.key],
										)
									: [],
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[footwearMaterialColumns.upper_material_type.key]}
					/>
					<RenderInputField
						column={{
							...footwearMaterialColumns.upper_material_pattern,
							[selectOptionsKeyName]: {
								type: arrType,
								list: pageData?.[upperMaterialColumn.key]
									? upperMaterialPatternOptions.filter(
											(item) => item[upperMaterialColumn.key] === pageData[upperMaterialColumn.key],
										)
									: [],
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[footwearMaterialColumns.upper_material_pattern.key]}
					/>
					<RenderInputField
						column={{
							...footwearMaterialColumns.sole_material,
							[selectOptionsKeyName]: {
								type: arrType,
								list: pageData?.[upperMaterialColumn.key]
									? soleMaterialOptions.filter(
											(item) => item[upperMaterialColumn.key] === pageData[upperMaterialColumn.key],
										)
									: [],
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[footwearMaterialColumns.sole_material.key]}
					/>
					<RenderInputField
						column={{
							...footwearMaterialColumns.insocks,
							[selectOptionsKeyName]: {
								type: arrType,
								list: pageData?.[upperMaterialColumn.key]
									? insocksOptions.filter(
											(item) => item[upperMaterialColumn.key] === pageData[upperMaterialColumn.key],
										)
									: [],
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[footwearMaterialColumns.insocks.key]}
					/>
				</Box>
			</Stack>
			{mode === "update" && selectedRow?.fw_material_code && (
				<Box sx={{ px: 2, pb: 1, width: "100%", boxSizing: "border-box", overflow: "hidden" }}>
					<Alert
						severity={
							impactLoading ? "info" : impactError ? "error" : (impact?.total_count ?? 0) > 0 ? "warning" : "info"
						}
						sx={impactAlertSx}
						action={
							!impactLoading && !impactError && (impact?.total_count ?? 0) > 0 ? (
								<Button color="inherit" size="small" onClick={() => setShowImpactStyles((prev) => !prev)}>
									{showImpactStyles ? "Hide" : "View"} styles
								</Button>
							) : undefined
						}>
						{impactLoading
							? "Loading style impact..."
							: impactError
								? impactError
								: (impact?.total_count ?? 0) === 0
									? "This change will affect 0 style(s)."
									: `This change will affect ${impact.total_count} style(s)${impact.published_count > 0 ? ` (${impact.published_count} published)` : ""}.`}
					</Alert>
					<Collapse in={showImpactStyles && impact?.styles?.length > 0}>
						<Stack spacing={0.5} sx={{ maxHeight: 120, overflow: "auto", pl: 1, mt: 1, boxSizing: "border-box" }}>
							{impact?.styles?.map((style) => (
								<Typography key={style.style_id} variant="body2" sx={{ wordBreak: "break-word" }}>
									{style.style_id} — {style.style_name} ({style.status})
								</Typography>
							))}
						</Stack>
					</Collapse>
				</Box>
			)}
			<DialogActions>
				<Button variant="outlined" onClick={handleCloseModal}>
					Cancel
				</Button>
				<Button autoFocus variant="contained" sx={{ mr: 1 }} onClick={handleSaveClick}>
					{mode === "create" ? "Create" : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CreateOrUpdateMaterialModal;
